/*
 * The two faded cutouts behind shift.patients-research: a doctor in the
 * Earlier card and a patient on her phone in the Now card, the shift from the
 * doctor deciding to the patient researching. Flat inline SVG in the site's
 * palette; decorative only (aria-hidden, next to the cards' own text).
 * The idle motion (breathing, a nod, the stethoscope's sway; scrolling and
 * search bubbles) lives in PatientsResearch.css and rests under
 * prefers-reduced-motion.
 */

const SKIN = '#f2c5a3';
const SKIN_SHADE = '#e2a684';
const INK = '#0b1d51';

export function DoctorFigure({ className }) {
  return (
    <svg className={className} viewBox="0 0 200 230" aria-hidden="true" focusable="false">
      <g className="fig-breathe">
        {/* neck, then the coat over it */}
        <path d="M88 96h24v34c0 6-24 6-24 0Z" fill={SKIN} />
        <path d="M88 108c8 6 16 6 24 0v8c-8 5-16 5-24 0Z" fill={SKIN_SHADE} />
        <path
          d="M28 230c1-48 10-80 40-94l20-13 12 30 12-30 20 13c30 14 39 46 40 94Z"
          fill="#fff"
          stroke="#cfdbef"
          strokeWidth="1.5"
        />
        {/* soft fold shading down each side of the coat */}
        <path d="M38 230c2-34 8-56 20-70-4 26-4 48-2 70Z" fill="#e9eff9" />
        <path d="M162 230c-2-34-8-56-20-70 4 26 4 48 2 70Z" fill="#e9eff9" />
        {/* shirt and tie in the V */}
        <path d="M88 123 100 182 112 123c-6 5-18 5-24 0Z" fill="#d6e4ff" />
        <path d="M95 128h10l-2 8h-6Z" fill="#1463f3" />
        <path d="M97 136h6l3 32-6 9-6-9Z" fill="#1463f3" />
        <path d="M88 123 95 128 92 134Z M112 123 105 128 108 134Z" fill="#fff" />
        {/* lapels */}
        <path d="M88 123 74 150 100 186Z" fill="#f3f7fd" stroke="#cfdbef" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M112 123 126 150 100 186Z" fill="#f3f7fd" stroke="#cfdbef" strokeWidth="1.5" strokeLinejoin="round" />
        {/* breast pocket with pens, and an ID badge */}
        <rect x="134" y="160" width="3.5" height="14" rx="1.5" fill="#1463f3" />
        <rect x="140" y="163" width="3.5" height="11" rx="1.5" fill={INK} />
        <path
          d="M128 170h26v18c0 2-1.5 3.5-3.5 3.5h-19c-2 0-3.5-1.5-3.5-3.5Z"
          fill="#fff"
          stroke="#cfdbef"
          strokeWidth="1.5"
        />
        <rect x="56" y="168" width="20" height="13" rx="2.5" fill="#eaf1fe" stroke="#b9cdf6" strokeWidth="1.2" />
        <rect x="59" y="172" width="8" height="1.8" rx="0.9" fill="#7aa2f7" />
        <rect x="59" y="176" width="13" height="1.8" rx="0.9" fill="#b9cdf6" />

        {/* stethoscope: the tube around the neck, the earpiece end on the right */}
        <path d="M89 120c-9 12-14 30-10 50" fill="none" stroke="#25345f" strokeWidth="4" strokeLinecap="round" />
        <path d="M111 120c10 12 13 26 9 40" fill="none" stroke="#25345f" strokeWidth="4" strokeLinecap="round" />
        <circle cx="120" cy="161" r="3.2" fill="#9fb0cf" />
        {/* the chest piece sways from the collar */}
        <g className="fig-sway">
          <path d="M79 170c-1 6 0 10 2 13" fill="none" stroke="#25345f" strokeWidth="4" strokeLinecap="round" />
          <circle cx="82" cy="190" r="8.5" fill="#c4cfe2" stroke="#8b9bbb" strokeWidth="1.6" />
          <circle cx="82" cy="190" r="4.2" fill="#e9eef7" />
        </g>
      </g>

      {/* a clipboard held against the side, its foot fading out of the cutout */}
      <g className="fig-clipboard">
        <g transform="rotate(10 156 206)">
          <rect x="134" y="178" width="44" height="58" rx="5" fill="#2f63d6" />
          <rect x="139" y="185" width="34" height="46" rx="2.5" fill="#fff" />
          <path d="M150 196h18M150 205h18M150 214h12" stroke="#c9d6ec" strokeWidth="2.4" strokeLinecap="round" />
          <path
            d="M143 195.5l2 2 3-3.5M143 204.5l2 2 3-3.5"
            fill="none"
            stroke="#1fa463"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="149" y="174" width="14" height="7" rx="2.5" fill="#8b9bbb" />
        </g>
        {/* the forearm rising from below, a cuff, and the hand on the board's edge */}
        <path
          d="M112 232c4-14 12-24 22-28l9 9c-8 4-13 11-15 21Z"
          fill="#fff"
          stroke="#cfdbef"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M131 205.5l9.5 9" stroke="#cfdbef" strokeWidth="4" strokeLinecap="round" />
        <path d="M134 200c4-4 10-4 13 0l2 9c-2 4-8 6-12 3Z" fill={SKIN} />
      </g>

      {/* the head nods gently from the neck */}
      <g transform="translate(0 5)">
        <g className="fig-nod">
          <ellipse cx="74.5" cy="78" rx="5" ry="7" fill={SKIN_SHADE} />
          <ellipse cx="125.5" cy="78" rx="5" ry="7" fill={SKIN_SHADE} />
          <ellipse cx="100" cy="74" rx="25" ry="29" fill={SKIN} />
          <path
            d="M75 74c-4-22 8-34 26-34 17 0 28 11 24 34-2-8-6-14-12-16-9 4-24 5-35 1-2 4-3 9-3 15Z"
            fill="#1c2541"
          />
          <path
            d="M86 70.5q5-3 9 0M105 70.5q5-3 9 0"
            fill="none"
            stroke="#1c2541"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="90.5" cy="78" r="2.3" fill="#1c2541" />
          <circle cx="109.5" cy="78" r="2.3" fill="#1c2541" />
          <path d="M100 81v6c0 1.2-1 2-2.4 2" fill="none" stroke={SKIN_SHADE} strokeWidth="1.8" strokeLinecap="round" />
          <path d="M93 93q7 5 14 0" fill="none" stroke="#b2664c" strokeWidth="2" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

const HOODIE = '#3d7bff';
const HOODIE_SHADE = '#2a62dc';
const HAIR = '#3a2723';

/* The phone's screen: a search bar and a list of results that scroll by. */
function PhoneScreen() {
  return (
    <>
      <defs>
        <clipPath id="fig-phone-screen">
          <rect x="79" y="133" width="22" height="36" rx="2.5" />
        </clipPath>
      </defs>
      <rect x="79" y="133" width="22" height="36" rx="2.5" fill="#f3f7fd" />
      <g clipPath="url(#fig-phone-screen)">
        <g className="fig-scroll">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <g key={i} transform={`translate(0 ${i * 12})`}>
              <rect x="81" y="143" width="5" height="5" rx="1.2" fill={i % 2 ? '#9fc0ff' : '#ffc979'} />
              <rect x="88" y="143.2" width="11" height="1.8" rx="0.9" fill="#7aa2f7" />
              <rect x="88" y="146.4" width="7" height="1.6" rx="0.8" fill="#c9d6ec" />
            </g>
          ))}
        </g>
        <rect x="79" y="133" width="22" height="8" fill="#f3f7fd" />
        <rect x="80.5" y="134.5" width="19" height="5" rx="2.5" fill="#fff" stroke={HOODIE} strokeWidth="0.8" />
        <circle cx="83.3" cy="137" r="1.2" fill="none" stroke={HOODIE} strokeWidth="0.7" />
      </g>
    </>
  );
}

/* A small search bubble: a magnifier and a line of query. */
function Bubble({ x, y, w, className }) {
  return (
    <g className={`fig-bubble ${className}`}>
      <rect x={x} y={y} width={w} height="13" rx="6.5" fill="#fff" stroke="#d6e2f6" strokeWidth="1" />
      <circle cx={x + 6.5} cy={y + 6} r="2.4" fill="none" stroke={HOODIE} strokeWidth="1.3" />
      <path d={`M${x + 8.2} ${y + 7.8}l1.8 1.8`} stroke={HOODIE} strokeWidth="1.3" strokeLinecap="round" />
      <rect x={x + 12.5} y={y + 5.4} width={w - 17} height="2" rx="1" fill="#b9cdf6" />
    </g>
  );
}

export function PatientFigure({ className }) {
  return (
    <svg className={className} viewBox="0 0 180 200" aria-hidden="true" focusable="false">
      {/* search bubbles rising from the phone */}
      <Bubble x={112} y={96} w={46} className="fig-bubble--a" />
      <Bubble x={20} y={84} w={40} className="fig-bubble--b" />
      <Bubble x={118} y={60} w={36} className="fig-bubble--c" />

      <g className="fig-breathe">
        {/* hood behind the neck, the neck, then the hoodie */}
        <path d="M62 112c4-16 52-16 56 0-8 10-48 10-56 0Z" fill={HOODIE_SHADE} />
        <path d="M80 86h20v26c0 5-20 5-20 0Z" fill={SKIN} />
        <path d="M80 96c6 5 14 5 20 0v7c-6 4-14 4-20 0Z" fill={SKIN_SHADE} />
        <path d="M24 200c1-42 10-70 38-84 6 8 50 8 56 0 28 14 37 42 38 84Z" fill={HOODIE} />
        <path d="M62 116c6 8 50 8 56 0l4 3c-8 12-56 12-64 0Z" fill={HOODIE_SHADE} />
        {/* drawstrings */}
        <path d="M82 120l-2 22M98 120l2 20" stroke="#eaf1fe" strokeWidth="2" strokeLinecap="round" />
        <circle cx="80" cy="144" r="1.8" fill="#eaf1fe" />
        <circle cx="100" cy="142" r="1.8" fill="#eaf1fe" />
        {/* front pocket seam */}
        <path
          d="M54 196c4-16 14-22 36-22s32 6 36 22"
          fill="none"
          stroke={HOODIE_SHADE}
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* both forearms come in to hold the phone */}
        <path d="M38 188c4-22 20-34 40-32l2 14c-14-1-24 6-30 20Z" fill={HOODIE_SHADE} />
        <path d="M142 188c-4-22-20-34-40-32l-2 14c14-1 24 6 30 20Z" fill={HOODIE_SHADE} />

        {/* the phone, screen to us */}
        <rect x="76" y="128" width="28" height="46" rx="5" fill={INK} />
        <PhoneScreen />
        <ellipse cx="76" cy="162" rx="6" ry="8" fill={SKIN} />
        <ellipse cx="104" cy="162" rx="6" ry="8" fill={SKIN} />
        {/* the thumb, scrolling */}
        <g className="fig-thumb">
          <ellipse cx="99" cy="158" rx="3.2" ry="4.6" fill={SKIN_SHADE} transform="rotate(-25 99 158)" />
        </g>
      </g>

      {/* the head tilts down towards the phone, with a little idle bob */}
      <g className="fig-nod fig-nod--patient">
        <path d="M67 70c-3-26 9-40 24-40 16 0 27 13 24 38-1 5-3 9-5 12-2-14-38-14-41 2-1-4-2-8-2-12Z" fill={HAIR} />
        <circle cx="104" cy="30" r="11" fill={HAIR} />
        <ellipse cx="68.5" cy="70" rx="4.4" ry="6" fill={SKIN_SHADE} />
        <ellipse cx="111.5" cy="70" rx="4.4" ry="6" fill={SKIN_SHADE} />
        <ellipse cx="90" cy="68" rx="21" ry="24" fill={SKIN} />
        <path d="M69 64c0-18 10-28 23-28 12 0 20 8 20 20-10-2-20-8-25-14-4 8-10 16-18 22Z" fill={HAIR} />
        {/* eyes down at the screen */}
        <path
          d="M80 72q3 2.4 6 0M95 72q3 2.4 6 0"
          fill="none"
          stroke="#1c2541"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path d="M86 84q4.5 3 9 0" fill="none" stroke="#b2664c" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="78" cy="80" r="3.2" fill="#f4a3a0" opacity="0.45" />
        <circle cx="102" cy="80" r="3.2" fill="#f4a3a0" opacity="0.45" />
      </g>
    </svg>
  );
}
